import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOrgTimezone, formatDateTimeForInput, dateTimeLocalToISO } from '@/utils/timezone';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { JobNameAutocomplete, JobTargetSelector, ActivityTypePicker } from '../../components/jobs';
import { ClipboardList, ArrowLeft } from 'lucide-react';
import jobService from '../../services/jobs';
import type { Job, JobTargetInput } from '../../types/jobs';

function JobEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [activityTypeId, setActivityTypeId] = useState<number | null>(null);
  const [description, setDescription] = useState('');
  const [targets, setTargets] = useState<JobTargetInput[]>([]);
  const [enableGps, setEnableGps] = useState(true);
  const [scheduledStart, setScheduledStart] = useState('');
  const [scheduledEnd, setScheduledEnd] = useState('');
  const [notes, setNotes] = useState('');

  const orgTz = useOrgTimezone();

  const jobId = parseInt(id || '0', 10);

  useEffect(() => {
    jobService.getJob(jobId).then(res => {
      if (res.success) {
        const j = res.data;
        setJob(j);
        setName(j.name);
        setActivityTypeId(j.activity_type_id ?? null);
        setDescription(j.description || '');
        // Load targets from job_targets if available, fall back to legacy single target
        if (j.targets && j.targets.length > 0) {
          setTargets(j.targets.map(t => ({ type: t.target_type, id: t.target_id })));
        } else if (j.target_type && j.target_id) {
          setTargets([{ type: j.target_type, id: j.target_id }]);
        }
        setEnableGps(j.enable_gps_tracking);
        setScheduledStart(j.scheduled_start ? formatDateTimeForInput(j.scheduled_start, orgTz) : '');
        setScheduledEnd(j.scheduled_end ? formatDateTimeForInput(j.scheduled_end, orgTz) : '');
        setNotes(j.notes || '');
      }
    }).finally(() => setLoading(false));
  }, [jobId, orgTz]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await jobService.updateJob(jobId, {
        name: name.trim(),
        activity_type_id: activityTypeId,
        description: description.trim() || undefined,
        targets,
        enable_gps_tracking: enableGps,
        scheduled_start: scheduledStart ? dateTimeLocalToISO(scheduledStart, orgTz) : null,
        scheduled_end: scheduledEnd ? dateTimeLocalToISO(scheduledEnd, orgTz) : null,
        notes: notes.trim() || undefined,
      });
      if (res.success) navigate(`/jobs/${jobId}`);
    } catch (e: any) {
      setError(e?.message || 'Failed to update job');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="max-w-3xl mx-auto px-4 py-12 text-center"><div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" /></div>;
  }

  if (!job || !['created', 'assigned'].includes(job.status)) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <p className="text-neutral-500">{job ? 'This job can no longer be edited' : 'Job not found'}</p>
        <Button variant="outline" size="sm" className="mt-4" onClick={() => navigate('/jobs')}>Back to Jobs</Button>
      </div>
    );
  }

  return (
    <>
      <PageHeader title="Edit Job" icon={<ClipboardList className="h-6 w-6 text-white" />} />
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="mb-4">
          <button onClick={() => navigate(`/jobs/${jobId}`)} className="flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700">
            <ArrowLeft className="h-4 w-4" /> Back to Job
          </button>
        </div>

        <div className="bg-white border border-neutral-200 rounded-xl p-6 space-y-4">
          <ActivityTypePicker value={activityTypeId} onChange={setActivityTypeId} />

          <JobNameAutocomplete value={name} onChange={setName} />

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200" />
          </div>

          <JobTargetSelector targets={targets} onChange={setTargets} />

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={enableGps} onChange={e => setEnableGps(e.target.checked)}
              className="rounded border-neutral-300 text-indigo-600 focus:ring-indigo-200" />
            <span className="text-neutral-700">Enable GPS tracking</span>
          </label>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Start Date</label>
              <input type="datetime-local" value={scheduledStart} onChange={e => setScheduledStart(e.target.value)}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200" />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">End Date</label>
              <input type="datetime-local" value={scheduledEnd} onChange={e => setScheduledEnd(e.target.value)}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200" />
          </div>

          {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>}

          <div className="flex justify-end gap-2 pt-4 border-t border-neutral-100">
            <Button variant="outline" size="sm" onClick={() => navigate(`/jobs/${jobId}`)}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={handleSave} disabled={saving || !name.trim()}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

export default JobEdit;
